import React, { useState, useMemo } from 'react';
import { BarChart3, ChevronDown, Clock, Hourglass, Loader2, Puzzle } from 'lucide-react';
import { TimeOfDayDisplayProps, TimeOfDayConfig, TimeMetricType } from './types';
import {
  addPeakHourIndicators,
  analyzePeakHours,
  processHourlyDataForChart,
} from './utils';
import TimeOfDayChart from './time-of-day-chart';
import TimeInsightsPanel from './time-insights-panel';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from 'src/components/ui/dropdown-menu';
import { Button } from 'src/components/ui/button';

const AVAILABLE_METRICS = ['Impressions', 'Clicks', 'Spend'];

const defaultConfig: TimeOfDayConfig = {
  selectedMetric: 'Impressions' as TimeMetricType,
  chartType: 'bar',
  showPeakHours: true,
  show24Hour: true,
  showGrid: true,
  showTooltips: true,
  chartHeight: 400,
  colorScheme: 'blue',
  highlightPeakHours: true,
  showAverageLines: true,
};

const TimeOfDayDisplay: React.FC<TimeOfDayDisplayProps> = ({
  data,
  config: userConfig = {},
  onHourClick,
  className = '',
  isLoading = false,
  title = 'Time-of-Day Performance',
}) => {
  const [config, setConfig] = useState<TimeOfDayConfig>({ ...defaultConfig, ...userConfig });
  const [showInsights, setShowInsights] = useState(false);

  // Process data for chart
  const chartData = useMemo(() => {
    const processed = processHourlyDataForChart(data, config.selectedMetric, config.show24Hour);
    // return processed;
    const peakAnalysis = analyzePeakHours(data, config.selectedMetric);
    return addPeakHourIndicators(processed, peakAnalysis);
  }, [data, config.selectedMetric, config.show24Hour]);


  // Peak analysis
  const peakAnalysis = useMemo(() => {
    return analyzePeakHours(data, config.selectedMetric);
  }, [data, config.selectedMetric]);

  // Get available metrics from data
  const getAvailableMetrics = (): TimeMetricType[] => {
    const baseMetrics: TimeMetricType[] = ['clicks', 'impressions', 'spend', 'conversions'];
    const calculatedMetrics: TimeMetricType[] = [];

    // Check if we have enough data for calculated metrics
    const hasClicksAndImpressions = data.some(d => d.clicks > 0 && d.impressions > 0);
    const hasSpendAndClicks = data.some(d => d.spend > 0 && d.clicks > 0);
    const hasSpendAndConversions = data.some(d => d.spend > 0 && d.conversions > 0);

    if (hasClicksAndImpressions) calculatedMetrics.push('ctr', 'cvr');
    if (hasSpendAndClicks) calculatedMetrics.push('cpc');
    if (hasSpendAndConversions) calculatedMetrics.push('cpa');
    if (data.some(d => d.impressions > 0)) calculatedMetrics.push('cpm');

    return [...baseMetrics, ...calculatedMetrics];
  };
  const handleMetricChange = (metric: TimeMetricType) => {
    setConfig(prev => ({ ...prev, selectedMetric: metric }));
  };

  const handleChartTypeChange = (chartType: TimeOfDayConfig['chartType']) => {
    setConfig(prev => ({ ...prev, chartType }));
  };

  const handleHourClick = (hour: number, hourData: any) => {
    const fullHourData = data.find(d => d.hour === hour);
    if (fullHourData && onHourClick) {
      onHourClick(hour, fullHourData);
    }
  };

  const handleConfigChange = (key: keyof TimeOfDayConfig, value: any) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  if (isLoading) {
    return (
      <div className={`bg-white rounded-lg shadow-md border border-gray-200 p-8 ${className}`}>
        <div className="flex flex-col items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Loading Time-of-Day Data
          </h3>
          <p className="text-sm text-gray-600 text-center">
            Processing hourly metrics and generating insights...
          </p>
        </div>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className={`bg-white rounded-lg shadow-md border border-gray-200 p-8 ${className}`}>
        <div className="flex flex-col items-center justify-center h-64">
          <Clock className="w-12 h-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Hourly Data
          </h3>
          <p className="text-sm text-gray-600 text-center">
            No time-of-day metrics data available to display.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-md overflow-hidden ${className}`}>
      {/* Header */}
      <div className="flex justify-between bg-gray-201">
        <div className="flex items-center">
          <div className="flex gap-2 p-2 pl-4 items-center font-medium">
            <Hourglass className="w-4 h-4" />
            {title}
          </div>
          <div className="border-l border-gray-401 h-1/2 ml-4 mr-2" />
          <div className="flex items-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="med"
                >
                  <ChevronDown className="w-4 h-4" />
                  {AVAILABLE_METRICS.find((metric) => metric.toLowerCase() === config.selectedMetric.toLowerCase())}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className='z-[999]'>
                {AVAILABLE_METRICS.map((metric) => (
                  <DropdownMenuItem
                    key={metric}
                    onClick={() => handleMetricChange(metric.toLowerCase() as TimeMetricType)}
                  >
                    {metric}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className="flex gap-2 pr-4 items-center font-normal text-sm">
          {showInsights ? (
            <Button
              variant="ghost"
              size="med"
              onClick={() => setShowInsights(false)}
            >
              <BarChart3 className="w-4 h-4" />
              Graph
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="med"
              onClick={() => setShowInsights(true)}
            >
              <Puzzle className="w-4 h-4" />
              Insights
            </Button>
          )}
        </div>
      </div>

      {/* Chart */}
      {!showInsights && (
        <div className="mt-2">
          <TimeOfDayChart
            data={chartData || []}
            config={config}
            onHourClick={handleHourClick}
          />
        </div>
      )}

      {/* Insights Panel */}
      {showInsights && (
        <div className="">
          <TimeInsightsPanel
            data={data}
            selectedMetric={config.selectedMetric}
            peakAnalysis={peakAnalysis}
            use24Hour={config.show24Hour}
          />
        </div>
      )}
    </div>
  );
};

export default TimeOfDayDisplay;
